import BaseModel from './BaseModel'
import ChannelDefaultRate from './ChannelDefaultRate'
import LocationProduct from './LocationProduct'
import ParentSpotRate from './ParentSpotRate'
import { SpotRate } from './SpotRate'
import { VenueLocationProduct } from './VenueLocationProduct'
import { currencyFormatter } from '../utils/formatters'
import { Temporal } from '@js-temporal/polyfill'
import type { RateUnit } from '../types/misc'
import apiEndpoint from '../utils/apiEndpoint'
import { buildApiUrl } from '../utils/apiUtils'

const API_URL = import.meta.env.VITE_API_URL

export class DefaultRate extends BaseModel {

  static entity = 'default_rates'
  static primaryKey = 'ID'

  declare ID: number
  declare locationProductID: number|null
  declare defaultCost: number
  declare perQuantity: number|null
  declare perUnit: string|null
  declare channelDefaultRates: ChannelDefaultRate[]
  declare locationProduct: LocationProduct
  declare parentSpotRates: ParentSpotRate[]
  declare spotRates: SpotRate[]
  declare venueLocationProducts: VenueLocationProduct[]

  static fields() {

    return {

      // fields
      ID: this.number(null),
      locationProductID: this.number(null).nullable(),
      defaultCost: this.number(null),
      perQuantity: this.number(null).nullable(),
      perUnit: this.string(null).nullable(),

      // relationships
      channelDefaultRates: this.hasMany(ChannelDefaultRate, 'defaultRateID'),
      locationProduct: this.belongsTo(LocationProduct, 'locationProductID'),
      parentSpotRates: this.hasMany(ParentSpotRate, 'defaultRateID'),
      spotRates: this.hasMany(SpotRate, 'defaultRateID'),
      venueLocationProducts: this.hasMany(VenueLocationProduct, 'defaultRateID'),

    }

  }

  getDurationInHours(): number|null {
    if (!this.perQuantity || !this.perUnit) {
      return null
    }
    return this.perUnit === 'day' ? this.perQuantity * 24 : this.perQuantity
  }

  /**
   * @todo i18n
   */
  getTitle(): string {
    let title = currencyFormatter.format(this.defaultCost)
    if (this.perQuantity) {
      title += ' for ' + this.perQuantity + ' ' + this.perUnit + (this.perQuantity > 1 ? 's' : '')
    }
    return title
  }

  /**
   * API methods
   */

  static bulkCreate(locationID: number, payload: object, bypassWorkers: boolean = false) {

    /**
     * The expected payload is an array of objects like this:
     * 
     * {
     *   "locationProductID": 123,
     *   "defaultCost": 19.95,
     *   "perQuantity": 1,
     *   "perUnit": "hour",
     *   "channelDefaultRates": [
     *     {
     *       "channelCost": 17.00,
     *       "channelFacilitiesID": 456
     *     }
     *   ]
     * }
     * 
     * The created DefaultRate gets saved to the Vuex store - but not the
     * ChannelDefaultRates, because the API doesn't return those.
     */

    const url = `/rates/locations/${locationID}/default_rates`

    return this.api().post(url, {
      defaultRates: payload,
      bypassWorkers: bypassWorkers,
    })

  }

  static bulkUpdate(payload: BulkUpdateDefaultRatesRequest, bypassWorkers: boolean = false) {

    let url = '/rates/bulk_update'
    if (bypassWorkers) {
      url += '?bypassWorkers=1'
    }

    return this.api().post(url, {defaultRate: payload}, {
      dataKey: 'defaultRate',
    })

  }

  static destroy(defaultRateID: number, bypassWorkers: boolean = false) {

    let url = `/rates/default_rate/${defaultRateID}`

    if (bypassWorkers) {
      url += '?bypassWorkers=1'
    }

    return this.api().delete(url, {
      delete: defaultRateID,
    })

  }

  static getByLocationId({
    locationId,
    vertical,
    start,
    end,
    includeEventTiers = false,
    signal
  }: GetRatesByLocationIdRequest): Promise<GetRatesByLocationIdResponse> {

    const filters = {
      vertical,
      start: start?.toString(),
      end: end?.toString(),
      include_event_tiers: includeEventTiers,
    }

    const url = buildApiUrl(`${API_URL}/rates/locations/${locationId}/rates`, { filters })

    return apiEndpoint({url: url, method: 'get', }, signal)
      .then((response) => {

        if (Array.isArray(response.inventories_with_event_tiers)) {
          response.inventories_with_event_tiers.map((iwet: any) => {
            iwet.date = Temporal.PlainDate.from(iwet.date)
            iwet.event.eventStartLocal = Temporal.PlainDateTime.from(iwet.event.eventStartLocal)
            iwet.event.eventEndLocal = Temporal.PlainDateTime.from(iwet.event.eventEndLocal)
          })
        }

        return response

      })

  }

}

export interface GetRatesByLocationIdRequest {
  locationId: number
  vertical?: string
  start?: Temporal.PlainDate
  end?: Temporal.PlainDate
  includeEventTiers?: boolean
  signal?: AbortSignal
}

export interface GetRatesByLocationIdResponse {
  default_rates: {
    ID: number
    locationProductID: number
    defaultCost: number
    perQuantity: null | number
    perUnit: null | RateUnit
  }[]
  spot_rates: {
    ID: number | string
    defaultRateID: number
    parentSpotRateID: number
    channelFacilityID: null | number
    channel: null | string
    locationProductID: number
    startDate: string
    endDate: string
    spotCost: number
    eventID: null | number
    eventStartUtc: null | string
    eventStartLocal: null | string
    eventEndUtc: null | string
    eventEndLocal: null | string
    eventName: null | string
    venueID: null | number
    venueName: null | string
  }[]
  channel_default_rates: {
    ID: number
    defaultRateID: number
    channelCost: number
    channel: string
    channelFacilitiesID: number
    channelRateData: any
    status: string
  }[]
  inventories_with_event_tiers: null | InventoryWithEventTiers[]
}

export interface InventoryWithEventTiers {
  ID: number
  date: Temporal.PlainDate
  locationProductID: number
  eventID: number
  spacesTotal: number
  event: {
    ID: number
    venueID: number
    name: string
    eventStartLocal: Temporal.PlainDateTime
    eventEndLocal: Temporal.PlainDateTime
    venue: {
      id: number
      name: string
      formatted_address: string
      timezone: string
    }
  }
  eventTiers: {
    ID: number
    inventoryID: number
    tierOrder: number
    tierPrice: number
    tierInventoryPercentage: number
  }[]
  eventInventoryTier: {
    ID: number
    inventoryID: number
    eventID: number
    venueLocationProductID: number
    currentVenueTierID: null | number
    currentEventTierID: null | number
  }
}

export interface BulkUpdateDefaultRatesRequestItem {
  defaultRateID: number
  defaultCost: number
  channelDefaultRates?: {
    channelDefaultRateID?: number
    channelCost: number
    channelFacilitiesID?: number // this is defined as optional in the JSON schema on the BE, but I don't think it should be?
  }[]
  spotRates?: {
    spotRateID: number
    spotCost: number
    // TODO: use Temporal.PlainDate for startDate and endDate (instead of strings)? (this would be a breaking change to the bulkUpdate() method above)
    // these are defined as optional in the JSON schema on the BE, but I don't think they should be?
    startDate?: string
    endDate?: string
  }[]
}

export type BulkUpdateDefaultRatesRequest = BulkUpdateDefaultRatesRequestItem | BulkUpdateDefaultRatesRequestItem[]
